import * as React from 'react';
import {FunctionComponent, useState} from 'react';
import {GraphReportRow, ReportRowType, TableReportRow, TrendTemplate} from '../Models/ReportTemplate';
import {ReportField, reportFieldToLabel} from '../../Models/ReportField';
import {TemplateCards} from '../TemplateCards';

interface OwnProps {
    isAdding?: boolean;
    suggestTemplates?: boolean;
    trendTemplate?: TrendTemplate;
    onFinish: (trendTemplate: TrendTemplate) => void;
    onCancel: () => void;
    onDelete: () => void;
}

export const TrendTemplateForm: FunctionComponent<OwnProps> = ({
    isAdding,
    suggestTemplates,
    trendTemplate,
    onFinish,
    onCancel,
    onDelete,
}) => {
    const [showTemplates, setShowTemplates] = useState(suggestTemplates && isAdding);
    const [title, setTitle] = useState(trendTemplate?.title || '');
    const [tableFields, setTableFields] = useState<ReportField[]>(
        trendTemplate?.rows.find((row): row is TableReportRow => row.type === 'table')?.fields || [],
    );
    const [graphs, setGraphs] = useState<GraphReportRow[]>(
        trendTemplate?.rows.filter((row): row is GraphReportRow => row.type === 'graph') || [],
    );

    function validates() {
        return tableFields.length > 0 && title.length > 0;
    }

    const notSelectedReportFields = Object.values(ReportField).filter((field) => !tableFields.includes(field));
    const reportFields = [...tableFields, ...notSelectedReportFields];

    if (showTemplates) {
        return (
            <div className="card">
                <div className="card-title">
                    <h5>Add</h5>
                </div>
                <div className="card-body">
                    <TemplateCards startAddingTrend={() => setShowTemplates(false)} addTrendTemplate={onFinish} />
                    <div className="row mt-3">
                        <div className="col-12 d-flex justify-content-start">
                            <button className="btn btn-transparent pull-left" onClick={() => onCancel()}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="card">
            <div className="card-title">
                <h5>{isAdding ? 'Add trend' : 'Edit trend'}</h5>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-12">
                        <div className="mb-3">
                            <label htmlFor="title" className="form-label mb-2">
                                Title
                            </label>
                            <input
                                id="title"
                                type="text"
                                className="form-control"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                            />
                        </div>
                    </div>
                </div>
                <div className="row border-top border-bottom">
                    <div className="col-4 border-end pb-3">
                        <h5 className="mt-3 mb-3">Select table data</h5>
                        <div className="d-flex flex-column gap-2">
                            {reportFields.map((field) => (
                                <div key={field} className="form-check">
                                    <input
                                        type="checkbox"
                                        id={field}
                                        name={field}
                                        className="form-autosubmit form-check-input"
                                        checked={tableFields.includes(field)}
                                        onChange={() => {
                                            if (tableFields.includes(field)) {
                                                setTableFields(tableFields.filter((f) => f !== field));
                                            } else {
                                                setTableFields([...tableFields, field]);
                                            }
                                        }}
                                    />
                                    <label htmlFor={field} className="form-check-label">
                                        <span>{reportFieldToLabel(field)}</span>
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="col-8 pb-3">
                        <h5 className="mt-3 mb-3">Configure graphs</h5>
                        {graphs.map((graph, index) => (
                            <div key={index} className="row">
                                <div className="col-12 d-flex justify-content-between">
                                    <strong>Graph {index + 1}</strong>
                                    <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() =>
                                            setGraphs([...graphs.slice(0, index), ...graphs.slice(index + 1)])
                                        }
                                    >
                                        Delete
                                    </button>
                                </div>
                                <div className="col-12">
                                    <div className="mb-3">
                                        <label htmlFor={'graph-' + index + 'x'} className="form-label mb-2">
                                            X axis
                                        </label>
                                        <select
                                            className="form-select"
                                            value={graph.x}
                                            onChange={(e) => {
                                                setGraphs([
                                                    ...graphs.slice(0, index),
                                                    {...graph, x: e.target.value as ReportField},
                                                    ...graphs.slice(index + 1),
                                                ]);
                                            }}
                                        >
                                            {reportFields.map((field) => (
                                                <option key={field} value={field}>
                                                    {reportFieldToLabel(field)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor={'graph-' + index + 'y'} className="form-label mb-2">
                                            Y axis
                                        </label>
                                        <select
                                            className="form-select"
                                            value={graph.y}
                                            onChange={(e) => {
                                                setGraphs([
                                                    ...graphs.slice(0, index),
                                                    {...graph, y: e.target.value as ReportField},
                                                    ...graphs.slice(index + 1),
                                                ]);
                                            }}
                                        >
                                            {reportFields.map((field) => (
                                                <option key={field} value={field}>
                                                    {reportFieldToLabel(field)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        ))}
                        <div className="row">
                            <div className="col-12">
                                <button
                                    className="btn btn-wide btn-outline-secondary"
                                    onClick={() =>
                                        setGraphs([
                                            ...graphs,
                                            {
                                                type: ReportRowType.Graph,
                                                x: ReportField.ExecutedAt,
                                                y: ReportField.AveragePower,
                                            },
                                        ])
                                    }
                                >
                                    Add graph
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row mt-3">
                    <div className="col-12 d-flex justify-content-between">
                        <button className="btn btn-transparent pull-left" onClick={() => onCancel()}>
                            Cancel
                        </button>
                        <span className="d-flex gap-2">
                            {trendTemplate && (
                                <button
                                    className="btn btn-danger"
                                    disabled={!validates()}
                                    onClick={() => {
                                        if (confirm('Are you sure you want to delete this trend?')) {
                                            onDelete();
                                        }
                                    }}
                                >
                                    Delete
                                </button>
                            )}
                            <button
                                className="btn btn-primary"
                                disabled={!validates()}
                                onClick={() =>
                                    onFinish({
                                        title: title,
                                        rows: [
                                            {
                                                type: ReportRowType.Table,
                                                fields: tableFields,
                                            },
                                            ...graphs,
                                        ],
                                    })
                                }
                            >
                                Save
                            </button>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};
